import { useMutation, useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { aiCrmApi, type CrmBriefKind } from '../../api/aiCrm';
import { useCompany } from '../../contexts/CompanyContext';

/**
 * "Catch me up" — a short briefing written from this record's own history.
 *
 * Three deliberate choices: it never runs on load (an AI call costs money and
 * nobody asked for it), it is labelled as written by AI so it is read as a
 * prompt to look rather than as a fact, and it disappears entirely when no
 * provider is configured. Nothing on the page depends on it.
 */
export default function AiBrief({ kind, id, label = 'Catch me up' }: { kind: CrmBriefKind; id: string; label?: string }) {
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const { data: available } = useQuery({
    queryKey: ['ai-crm-status', cid],
    queryFn: () => aiCrmApi.status(),
    enabled: !!cid,
    staleTime: 5 * 60 * 1000,
  });

  const brief = useMutation({ mutationFn: () => aiCrmApi.brief(kind, id) });

  if (!available) return null;

  return (
    <div className="card space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className="section-title flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-primary" /> {label}</h3>
        <button
          type="button"
          className="btn btn-secondary text-xs"
          onClick={() => brief.mutate()}
          disabled={brief.isPending}
        >
          {brief.isPending ? 'Reading…' : brief.data ? 'Again' : 'Summarize'}
        </button>
      </div>

      {brief.isPending && <p className="text-sm text-muted">Reading the history…</p>}

      {brief.data?.error && <p className="text-sm text-warn">{brief.data.error}</p>}

      {brief.data && !brief.data.error && (
        brief.data.summary ? (
          <>
            <p className="text-sm text-fg whitespace-pre-wrap">{brief.data.summary}</p>
            <p className="text-[11px] text-muted">Written by AI from the records on this page. Check anything you are about to act on.</p>
          </>
        ) : (
          <p className="text-sm text-muted">There is not enough recorded here to summarize yet.</p>
        )
      )}

      {!brief.data && !brief.isPending && (
        <p className="text-sm text-muted">A few sentences on where this stands, from its own history.</p>
      )}
    </div>
  );
}
